import type { DashboardId } from '@clinspace/shared';
import { RELATEDNESS } from '@clinspace/shared';
import { Dashboard, type ViewRegion } from '../Dashboard.ts';
import type { CanvasPanel } from '../CanvasPanel.ts';
import { PALETTE, hbars, heatmap, list, text, type Rect } from '../charts.ts';
import { aeDetail } from '../details.ts';
import { hoveredRow } from './Enrollment.ts';

/**
 * SAE Review: totals, unexpected, related, fatal; a timeline wall of SAE cards (3D boxes along
 * study time in front of the panel); severity vs relatedness grid; card -> narrative.
 */
export class SaeReviewDashboard extends Dashboard {
  readonly id: DashboardId = 'SAEReview';

  protected override segmentLabel(segment: string): string {
    const ae = this.view.ae(segment.slice(4));
    return ae ? `${ae.term} · ${ae.subjectId}` : segment;
  }

  protected drawContent(ctx: CanvasRenderingContext2D, panel: CanvasPanel, area: Rect): void {
    const [sae] = this.path;
    if (sae) {
      const ae = this.view.ae(sae.slice(4));
      if (ae) {
        this.detail = aeDetail(this.view, ae);
        this.drawDetail(ctx, panel, area, this.detail);
      }
      return;
    }
    this.detail = undefined;
    const saes = this.view.adverseEvents(this.filters).filter((a) => a.serious);
    const enrolled = this.view.subjects(this.filters).filter((s) => s.randomisationDate).length;
    const related = saes.filter((a) =>
      ['Possibly', 'Probably', 'Definitely'].includes(a.relatedness),
    ).length;
    this.kpis(
      panel,
      ctx,
      [
        { label: 'Total SAEs', value: String(saes.length), colour: PALETTE.crimson },
        {
          label: 'Unexpected',
          value: String(saes.filter((a) => !a.expected).length),
          colour: PALETTE.amber,
        },
        { label: 'Related', value: String(related) },
        {
          label: 'Fatal',
          value: String(saes.filter((a) => a.outcome === 'Fatal').length),
          colour: '#ff6b7a',
        },
        {
          label: 'Per 100 subjects',
          value: enrolled ? String(Math.round((saes.length / enrolled) * 1000) / 10) : '—',
        },
      ],
      { x: area.x, y: area.y, w: area.w, h: 76 },
    );
    const left: Rect = { x: area.x, y: area.y + 88, w: area.w * 0.58, h: area.h - 88 };
    text(
      ctx,
      'Timeline wall: the cards stand on the floor in front, left to right is study time',
      left.x,
      left.y + 14,
      { size: 11, colour: PALETTE.muted, weight: 600 },
    );
    const sorted = saes.slice().sort((a, b) => a.onsetDate.localeCompare(b.onsetDate));
    // On the wide centre screen (2026-10-07) the list takes the rows it needs and the space
    // under it shows where the SAEs happened and why they were serious.
    const extras = this.wide > 1 && left.h - 20 - (24 + sorted.length * 20) >= 130;
    const listH = extras ? 24 + Math.max(1, sorted.length) * 20 + 8 : left.h - 20;
    const rows = list(
      ctx,
      ['Onset', 'Subject', 'Term', 'Grade', 'Related', 'Criteria'],
      sorted.map((a) => ({
        cells: [
          a.onsetDate,
          a.subjectId,
          a.term,
          String(a.grade),
          a.relatedness,
          a.seriousnessCriteria.join(', '),
        ],
        flag: a.outcome === 'Fatal' || !a.expected ? 'x' : undefined,
        colour: a.outcome === 'Fatal' ? '#ff6b7a' : PALETTE.amber,
      })),
      { x: left.x, y: left.y + 20, w: left.w, h: listH },
      {
        widths: [0.14, 0.14, 0.3, 0.08, 0.14, 0.2],
        hoveredIndex: hoveredRow(panel, 'row:'),
        rowH: 20,
      },
    );
    rows.forEach((r, i) =>
      panel.region<ViewRegion>({
        id: `row:${i}`,
        ...r,
        data: { kind: 'drill', segment: `sae:${sorted[i]!.id}` },
        tooltip: [`${sorted[i]!.term} · ${sorted[i]!.subjectId}`, 'Open the narrative'],
      }),
    );
    if (extras)
      this.drawWideExtras(ctx, panel, saes, {
        x: left.x,
        y: left.y + 20 + listH + 8,
        w: left.w,
        h: left.h - 20 - listH - 8,
      });
    const right: Rect = {
      x: area.x + area.w * 0.62,
      y: area.y + 88,
      w: area.w * 0.38,
      h: area.h - 88,
    };
    text(ctx, 'Severity vs relatedness', right.x, right.y + 14, {
      size: 12,
      colour: PALETTE.muted,
      weight: 600,
    });
    const grades = [3, 4, 5];
    const cells = heatmap(
      ctx,
      grades.map((g) => `Grade ${g}`),
      [...RELATEDNESS],
      (r, c) =>
        saes.filter((a) => a.grade === grades[r] && a.relatedness === RELATEDNESS[c]).length ||
        undefined,
      { x: right.x, y: right.y + 20, w: right.w, h: right.h - 20 },
      { max: 3, labelWidth: 70, colour: (t) => `rgba(212, 58, 82, ${0.2 + 0.7 * t})` },
    );
    cells.forEach((row, r) =>
      row.forEach((rect, c) =>
        panel.region<ViewRegion>({
          id: `grid:${r}:${c}`,
          ...rect,
          data: { kind: 'info' },
          tooltip: [
            `Grade ${grades[r]} · ${RELATEDNESS[c]}`,
            `${saes.filter((a) => a.grade === grades[r] && a.relatedness === RELATEDNESS[c]).length} SAEs`,
          ],
          selectable: false,
        }),
      ),
    );
  }

  /** SAEs per site and per seriousness criterion, side by side (the wide screen only). */
  private drawWideExtras(
    ctx: CanvasRenderingContext2D,
    panel: CanvasPanel,
    saes: { siteId: string; seriousnessCriteria: string[] }[],
    area: Rect,
  ): void {
    const half = (area.w - 24) / 2;
    const bySite = this.view.file.sites
      .map((s) => ({ id: s.id, label: s.city, n: saes.filter((a) => a.siteId === s.id).length }))
      .sort((a, b) => b.n - a.n);
    const siteRows = hbars(
      ctx,
      bySite.map((s) => ({ label: s.label, value: s.n, colour: PALETTE.crimson })),
      { x: area.x, y: area.y, w: half, h: area.h },
      { title: 'SAEs per site', labelWidth: 96, hoveredIndex: hoveredRow(panel, 'saesite:') },
    );
    siteRows.forEach((r, i) =>
      panel.region<ViewRegion>({
        id: `saesite:${i}`,
        ...r,
        data: { kind: 'info' },
        tooltip: [this.view.siteLabel(bySite[i]!.id), `${bySite[i]!.n} SAEs`],
        selectable: false,
      }),
    );
    const criteria = new Map<string, number>();
    for (const a of saes)
      for (const c of a.seriousnessCriteria) criteria.set(c, (criteria.get(c) ?? 0) + 1);
    const byCriterion = [...criteria.entries()].sort((a, b) => b[1] - a[1]);
    const critRows = hbars(
      ctx,
      byCriterion.map(([label, n]) => ({ label, value: n, colour: PALETTE.amber })),
      { x: area.x + half + 24, y: area.y, w: half, h: area.h },
      {
        title: 'Why they were serious',
        labelWidth: 150,
        hoveredIndex: hoveredRow(panel, 'saecrit:'),
      },
    );
    critRows.forEach((r, i) =>
      panel.region<ViewRegion>({
        id: `saecrit:${i}`,
        ...r,
        data: { kind: 'info' },
        tooltip: [byCriterion[i]![0], `${byCriterion[i]![1]} SAEs (one SAE can meet several)`],
        selectable: false,
      }),
    );
  }
}
